import React from 'react';
import { motion } from 'framer-motion';
import { ScrollReveal } from './ScrollReveal';
import { useClient } from './ClientContext';
const steps = [{
  id: '01',
  title: 'Discover',
  description: 'We start by understanding the product, audience and context. This includes the problem the brand needs to solve, how it will be used and what success looks like. The goal is shared clarity before any design decisions are made.',
  included: ['In person deep dive session', 'Desktop research', 'Competitor analysis', 'Key findings summary']
}, {
  id: '02',
  title: 'Define',
  description: 'We define the key decisions that shape the brand. This stage creates clarity around who the brand is for, what it needs to say, and how it should sound so design can move forward with confidence.',
  included: ['Clear target audience and focus', 'Core messages', 'Tone of voice principles', 'Design direction and boundaries']
}, {
  id: '03',
  title: 'Design',
  description: 'We create the core design foundations for the brand. This includes a clear visual direction and the essential elements needed to apply the brand with confidence.',
  included: ['Visual direction', 'Core brand elements', 'Ready to use design assets']
}, {
  id: '04',
  title: 'Deliver',
  description: 'We package and hand over the work clearly so the brand can be used with confidence from day one.',
  included: ['Final brand assets', 'Usage guidance', 'Clean handover']
}];
export function ApproachTimeline() {
  const {
    companyName
  } = useClient();
  return <section id="approach" className="py-20 md:py-28 px-4 md:px-6 border-b border-white/[0.06]" data-id="element-17">
      <div className="max-w-7xl mx-auto" data-id="element-18">
        <ScrollReveal data-id="element-19">
          <div className="mb-12 md:mb-16 flex flex-col gap-4" data-id="element-20">
            <h2 className="text-xs md:text-sm font-bold tracking-[0.2em] text-accent uppercase" data-id="element-21">
              {companyName} — Approach
            </h2>
            <p className="text-xl md:text-3xl text-white max-w-2xl font-light leading-relaxed" data-id="element-22">
              A systematic process designed to uncover truth and build value.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/[0.06]" data-id="element-23">
          {steps.map((step, index) => <ScrollReveal key={step.id} delay={index * 0.1} data-id="element-24">
              <motion.div className="flex flex-col h-full border-r border-b border-white/[0.06] p-6 md:p-8 hover:bg-white/[0.02] transition-colors duration-500" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.12,
            ease: [0.25, 0.1, 0.25, 1]
          }} data-id="element-25">
                <div className="flex flex-col h-full" data-id="element-26">
                  <div className="mb-6 md:mb-8" data-id="element-27">
                    <span className="block text-xs md:text-sm font-mono text-zinc-500 mb-3 md:mb-4 tracking-widest" data-id="element-28">
                      [{step.id}]
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight" data-id="element-29">
                      {step.title}
                    </h3>
                  </div>

                  <p className="text-zinc-400 text-sm md:text-base leading-relaxed flex-1 mb-6 md:mb-8" data-id="element-30">
                    {step.description}
                  </p>

                  <div className="mt-auto pt-5 md:pt-6 border-t border-white/[0.06]" data-id="element-31">
                    <h4 className="text-white text-[11px] font-bold tracking-widest uppercase mb-4 opacity-50" data-id="element-32">
                      Included
                    </h4>
                    <ul className="space-y-3" data-id="element-33">
                      {step.included.map((item, i) => <li key={i} className="flex items-start text-xs md:text-sm text-zinc-500 font-mono" data-id="element-34">
                          <span className="mr-3 text-accent" data-id="element-35">+</span>
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>)}
        </div>
      </div>
    </section>;
}